import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { branding } from '../../constants/branding';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { store } from '../../store';
import { adminApiService } from '../../services/adminApiService';
import StrategyExchangeInfo from '../trading/StrategyExchangeInfo';
import TradeEvaluationPanel from '../trading/TradeEvaluationPanel';
import { formatTradeClosedDisplay, mapTradesForEvaluation } from '../../utils/tradeWindowEvaluation';
import { useSagemasterEnabled } from '../../hooks/useSagemasterEnabled';

interface AdminAnalyticsData {
  botId?: number;
  strategyId?: number;
  name: string;
  status: string;
  totalInvested: number;
  currentValue: number;
  totalPnl: number;
  pnlPercentage: number;
  realizedPnl: number;
  maxDrawdown: number;
  activeUsers: number;
  totalAllocations: number;
  equityCurve: Array<{
    timestamp: string;
    value: number;
    drawdown: number;
  }>;
  pnlDistribution: Array<{
    range: string;
    count: number;
    percentage: number;
  }>;
  riskMetrics: {
    volatility: number;
    profitFactor: number;
    winRate: number;
    sharpeRatio: number;
  };
  lastUpdated: string;
}

interface UserLeaderboardData {
  userId: number;
  username: string;
  email: string;
  allocatedAmount: number;
  currentValue: number;
  totalPnl: number;
  pnlPercentage: number;
  realizedPnl: number;
  maxDrawdown: number;
  status: string;
  lastActivity: string;
  firstAllocation: string;
  allocationCount: number;
}

interface AdminAnalyticsTabsProps {
  type: 'bots' | 'strategies';
  selectedId: number;
  onClose: () => void;
}

const COLORS = ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe'];

const AdminAnalyticsTabs: React.FC<AdminAnalyticsTabsProps> = ({ type, selectedId, onClose }) => {
  console.log('AdminAnalyticsTabs: Component render', { type, selectedId });
  const sagemasterEnabled = useSagemasterEnabled('admin');

  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'risk' | 'distributions'>('overview');
  const [timeRange, setTimeRange] = useState('1M');
  const [analyticsData, setAnalyticsData] = useState<AdminAnalyticsData | null>(null);
  const [usersData, setUsersData] = useState<UserLeaderboardData[]>([]);
  const [loading, setLoading] = useState(type !== 'strategies'); // false for strategies, true for bots
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState('totalPnl');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Strategy-specific data
  const [strategyDetails, setStrategyDetails] = useState<any>(null);
  const [strategyTrades, setStrategyTrades] = useState<any[]>([]);
  const [strategyPositions, setStrategyPositions] = useState<any[]>([]);
  const [strategyPnlHistory, setStrategyPnlHistory] = useState<any[]>([]);
  const [strategyLoading, setStrategyLoading] = useState(true);
  const [forceRender, setForceRender] = useState(0);

  // Data source toggle: 'kucoin' = live KuCoin data (more accurate), 'db' = original DB data (rollback)
  // Default is 'db' until the kucoin-analytics backend endpoint is deployed to production.
  const [dataSource, setDataSource] = useState<'kucoin' | 'db'>('db');
  const dataSourceRef = React.useRef<'kucoin' | 'db'>('db');
  const [kucoinSummary, setKucoinSummary] = useState<any>(null);
  const [kucoinError, setKucoinError] = useState<string | null>(null);

  // KuCoin closed-trades pagination
  const [kucoinNextPageId, setKucoinNextPageId] = useState<string | null>(null);
  const [kucoinHasMore, setKucoinHasMore] = useState(false);
  const [kucoinLoadingMore, setKucoinLoadingMore] = useState(false);

  // DB rollback metadata & KuCoin import
  const [dbTradeMeta, setDbTradeMeta] = useState<{
    totalCloseRowsInDb?: number;
    returnedCount?: number;
    limit?: number;
  } | null>(null);
  const [dbSyncLoading, setDbSyncLoading] = useState(false);
  const [dbSyncMessage, setDbSyncMessage] = useState<string | null>(null);
  const [lastKucoinClosedCount, setLastKucoinClosedCount] = useState<number | null>(null);

  // Reset strategy loading state when selectedId or type changes
  useEffect(() => {
    if (type === 'strategies') {
      console.log('AdminAnalyticsTabs: Resetting strategy state for new strategy', selectedId);
      setStrategyLoading(true);
      setStrategyDetails(null);
      setStrategyTrades([]);
      setStrategyPositions([]);
      setStrategyPnlHistory([]);
      setKucoinSummary(null);
      setKucoinError(null);
      setKucoinNextPageId(null);
      setKucoinHasMore(false);
      setDbTradeMeta(null);
      setDbSyncMessage(null);
      setForceRender(0);
      didForceRenderOnLoad.current = false;
    }
  }, [selectedId, type]);

  // Re-fetch when data source toggle changes
  useEffect(() => {
    if (type === 'strategies') {
      setStrategyTrades([]);
      setStrategyPositions([]);
      setStrategyPnlHistory([]);
      setKucoinSummary(null);
      setKucoinError(null);
      setKucoinNextPageId(null);
      setKucoinHasMore(false);
      didForceRenderOnLoad.current = false;
      fetchStrategyAnalytics(false, dataSource);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource]);

  // Auto-refresh analytics data when modal is open (for strategies)
  useEffect(() => {
    if (type === 'strategies') {
      setLoading(false); // Use strategyLoading instead for strategies
      fetchStrategyAnalytics();
      
      // Set up interval to refresh every 10 seconds (silent refresh)
      const interval = setInterval(() => {
        if (type === 'strategies') {
          // Use ref so the interval always sees the current dataSource, not a stale closure value.
          // In KuCoin mode the silent refresh only updates open positions (never resets pagination).
          fetchStrategyAnalytics(true, dataSourceRef.current);
        }
      }, 10000); // 10 seconds
      
      // Cleanup interval when component unmounts or type changes
      return () => clearInterval(interval);
    } else {
      fetchAnalyticsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, timeRange, type]);

  // CRITICAL: Prevent modal from closing accidentally
  // This ensures onClose is not called unintentionally during data loading
  const handleClose = React.useCallback(() => {
    console.log('AdminAnalyticsTabs: handleClose called');
    onClose();
  }, [onClose]);

  // Debug: Log when strategyLoading changes
  useEffect(() => {
    if (type === 'strategies') {
      console.log('AdminAnalyticsTabs: strategyLoading changed to:', strategyLoading, {
        strategyDetails: !!strategyDetails,
        tradesCount: strategyTrades.length,
        positionsCount: strategyPositions.length,
        pnlCount: strategyPnlHistory.length
      });
      // Force a re-render check after state updates
      if (!strategyLoading) {
        console.log('AdminAnalyticsTabs: strategyLoading is false - component should render analytics now');
      }
    }
  }, [strategyLoading, type, strategyDetails, strategyTrades.length, strategyPositions.length, strategyPnlHistory.length]);

  // Debug: Log when forceRender changes (ensures we see re-renders)
  useEffect(() => {
    if (type === 'strategies' && forceRender > 0) {
      console.log('AdminAnalyticsTabs: forceRender triggered, component should re-render now', {
        forceRender,
        strategyLoading,
        hasDetails: !!strategyDetails,
        tradesCount: strategyTrades.length
      });
    }
  }, [forceRender, type, strategyLoading, strategyDetails, strategyTrades.length]);

  // Single safe re-render trigger: fires exactly once when loading completes and we have data.
  // Uses a ref to ensure it only fires the first time per load cycle (prevents runaway loops).
  const didForceRenderOnLoad = React.useRef(false);
  useEffect(() => {
    if (type === 'strategies' && !strategyLoading && strategyDetails) {
      if (!didForceRenderOnLoad.current) {
        didForceRenderOnLoad.current = true;
        setForceRender(prev => prev + 1);
      }
    } else {
      didForceRenderOnLoad.current = false;
    }
  }, [type, strategyLoading, strategyDetails]);

  useEffect(() => {
    if (type !== 'strategies') {
      fetchUsersData();
    }
  }, [selectedId, timeRange, page, sortBy, sortDirection]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const apiBaseUrl = getApiBaseUrl();
      const fullUrl = `${apiBaseUrl}/api/admin/analytics/${type}/${selectedId}?range=${timeRange}`;
      
      // Get auth token from store
      const state = store.getState();
      const token = state.auth.token;
      const authType = state.auth.type;
      
      console.log('AdminAnalyticsTabs: Fetching analytics data from:', fullUrl);
      console.log('AdminAnalyticsTabs: Auth type:', authType, 'Has token:', !!token);
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // Add Authorization header for JWT auth
      if (token && authType === 'JWT') {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(fullUrl, {
        credentials: 'include',
        headers,
      });
      
      console.log('AdminAnalyticsTabs: Response status:', response.status);
      
      if (!response.ok) {
        const responseText = await response.text();
        console.error('AdminAnalyticsTabs: Error response body:', responseText);
        throw new Error(`HTTP error! status: ${response.status}, body: ${responseText.substring(0, 200)}`);
      }
      
      const data = await response.json();
      console.log('AdminAnalyticsTabs: Response data:', data);
      
      if (data.status === 'Successful' || data.status === 'success') {
        const payload = data.data || {};
        const normalized: AdminAnalyticsData = {
          // keep existing fields if present
          ...(payload as any),
          equityCurve: Array.isArray(payload.equityCurve) ? payload.equityCurve : [],
          pnlDistribution: Array.isArray(payload.pnlDistribution) ? payload.pnlDistribution : [],
          riskMetrics: payload.riskMetrics || { volatility: 0, profitFactor: 0, winRate: 0, sharpeRatio: 0 },
          lastUpdated: payload.lastUpdated || new Date().toISOString()
        } as AdminAnalyticsData;
        setAnalyticsData(normalized);
      }
    } catch (error) {
      console.error('Failed to fetch analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStrategyAnalytics = async (silentRefresh: boolean = false, sourceOverride?: 'kucoin' | 'db') => {
    const activeSource = sourceOverride ?? dataSource;
    try {
      // Only show loading indicator on initial load, not on auto-refresh
      if (!silentRefresh) {
      setStrategyLoading(true);
      }
      console.log('fetchStrategyAnalytics: Starting fetch for strategy', selectedId, 'source:', activeSource);
      
      // Check auth state without dispatching — the HTTP interceptor handles token refresh automatically
      const { store } = await import('../../store');
      const state = store.getState();
      const isAuthenticated = state.auth.isAuthenticated;
      
      if (!isAuthenticated) {
        console.warn('fetchStrategyAnalytics: Not authenticated, cannot fetch analytics');
        setStrategyLoading(false);
        return;
      }
      
      // Fetch strategy details — the adminApiService interceptor handles auth/refresh automatically
      const strategyRes = await adminApiService.getStrategyDetails(selectedId);
      console.log('fetchStrategyAnalytics: Strategy details response:', strategyRes);
      
      if (strategyRes.success && strategyRes.data) {
        console.log('fetchStrategyAnalytics: Strategy details set:', strategyRes.data);
        // Use functional update to ensure state is set correctly
        setStrategyDetails(strategyRes.data);
      } else {
        console.warn('fetchStrategyAnalytics: Strategy details fetch not successful:', strategyRes);
        setStrategyDetails(null);
      }
      
      // Helper function to quickly compare arrays (faster than JSON.stringify)
      const arraysEqual = (a: any[], b: any[]): boolean => {
        if (!a || !b || a.length !== b.length) return false;
        if (a.length === 0) return true;
        if (a[0] !== b[0] || a[a.length - 1] !== b[b.length - 1]) return false;
        if (a.length <= 10) {
          return JSON.stringify(a) === JSON.stringify(b);
        }
        return a.every((item, i) => {
          const bItem = b[i];
          return item.timestamp === bItem?.timestamp &&
                 item.symbol === bItem?.symbol &&
                 item.pnl === bItem?.pnl;
        });
      };

      if (activeSource === 'kucoin') {
        // ----- KuCoin live data path -----
        setKucoinError(null);

        if (silentRefresh) {
          // Silent refresh in KuCoin mode: only update open positions.
          // Never reset closed trades or pagination cursors — that would wipe the user's loaded history.
          const positionsRes = await adminApiService.getStrategyPositions(selectedId);
          const livePositions = positionsRes.success && Array.isArray(positionsRes.data) ? positionsRes.data : [];
          setStrategyPositions(livePositions);
        } else {
          // Full fetch: closed-trade analytics AND live open positions in parallel
          const [kucoinRes, positionsRes] = await Promise.all([
            adminApiService.getStrategyKucoinAnalytics(selectedId, 200),
            adminApiService.getStrategyPositions(selectedId),
          ]);
          console.log('fetchStrategyAnalytics [KuCoin]: response:', kucoinRes);

          // Always apply live positions regardless of KuCoin closed-trade result
          const livePositions = positionsRes.success && Array.isArray(positionsRes.data) ? positionsRes.data : [];

          if (kucoinRes.success && kucoinRes.data) {
            const payload = kucoinRes.data as any;
            setKucoinSummary(payload.summary || null);
            if (payload.summary?.closedPositionCount != null) {
              setLastKucoinClosedCount(Number(payload.summary.closedPositionCount));
            }
            setStrategyPositions(livePositions);
            setStrategyPnlHistory(Array.isArray(payload.pnlHistory) ? payload.pnlHistory : []);

            // Map closedTrades → strategyTrades shape
            const mapTrades = (raw: any[]) => raw.map((t: any) => ({
              symbol: t.symbol,
              side: t.side === 'buy' ? 'LONG' : t.side === 'sell' ? 'SHORT' : t.side?.toUpperCase(),
              amount: t.size,
              price: t.closePrice,
              entryPrice: t.openPrice,
              pnl: t.pnl,
              exitReason: null,
              closedAt: t.closeTime,
              tradeFee: t.tradeFee,
              fundingFee: t.fundingFee,
              leverage: t.leverage,
              settleCurrency: t.settleCurrency,
              openTime: t.openTime,
              closeTime: t.closeTime,
            }));

            let trades = Array.isArray(payload.closedTrades) ? mapTrades(payload.closedTrades) : [];
            let hasMoreRemote = !!payload.hasMore;
            let nextCursor: string | null = payload.nextPageId || null;

            // If the first page has no matching trades but there are more pages,
            // auto-fetch older pages immediately (up to 10) so the table is never empty on load.
            if (trades.length === 0 && hasMoreRemote && nextCursor) {
              const MAX_AUTO_PAGES = 10;
              for (let attempt = 0; attempt < MAX_AUTO_PAGES && nextCursor && trades.length === 0; attempt++) {
                const moreRes = await adminApiService.getStrategyKucoinAnalytics(selectedId, 200, nextCursor);
                if (moreRes.success && moreRes.data) {
                  const mp = moreRes.data as any;
                  const moreTrades = Array.isArray(mp.closedTrades) ? mapTrades(mp.closedTrades) : [];
                  trades = [...trades, ...moreTrades];
                  hasMoreRemote = !!mp.hasMore;
                  nextCursor = mp.nextPageId || null;
                } else {
                  break;
                }
              }
            }

            setStrategyTrades(trades);
            setKucoinHasMore(hasMoreRemote);
            setKucoinNextPageId(nextCursor);
          } else {
            const errMsg = kucoinRes.error || 'Failed to load KuCoin analytics';
            console.warn('fetchStrategyAnalytics [KuCoin]: not successful:', kucoinRes);
            setKucoinError(errMsg);
            setKucoinSummary(null);
            setKucoinHasMore(false);
            setKucoinNextPageId(null);
            setStrategyTrades([]);
            setStrategyPositions(livePositions); // still show open positions on error
            setStrategyPnlHistory([]);
          }
        } // end else (full KuCoin fetch)
      } else {
        // ----- DB rollback path (original implementation) -----
        setKucoinSummary(null);
        setKucoinError(null);

        const [tradesRes, positionsRes, pnlRes] = await Promise.all([
          adminApiService.getStrategyTrades(selectedId, 200, { includeMeta: true }),
          adminApiService.getStrategyPositions(selectedId),
          adminApiService.getStrategyPnlHistory(selectedId, 30)
        ]);

        console.log('fetchStrategyAnalytics [DB]: Trades response:', tradesRes);
        console.log('fetchStrategyAnalytics [DB]: Positions response:', positionsRes);
        console.log('fetchStrategyAnalytics [DB]: PNL response:', pnlRes);

        React.startTransition(() => {
          if (tradesRes.success) {
            const payload = tradesRes.data as { trades?: any[]; meta?: Record<string, unknown> } | any[];
            const trades = Array.isArray(payload)
              ? payload
              : (Array.isArray(payload?.trades) ? payload.trades : []);
            setStrategyTrades(prev => arraysEqual(prev, trades) ? prev : trades);
            if (payload && !Array.isArray(payload) && payload.meta) {
              setDbTradeMeta({
                totalCloseRowsInDb: Number(payload.meta.totalCloseRowsInDb ?? 0),
                returnedCount: Number(payload.meta.returnedCount ?? trades.length),
                limit: Number(payload.meta.limit ?? 200),
              });
            } else {
              setDbTradeMeta({ returnedCount: trades.length, totalCloseRowsInDb: trades.length });
            }
          } else {
            setStrategyTrades([]);
            setDbTradeMeta(null);
          }

          if (positionsRes.success) {
            const positions = Array.isArray(positionsRes.data) ? positionsRes.data : [];
            setStrategyPositions(prev => arraysEqual(prev, positions) ? prev : positions);
          } else {
            setStrategyPositions([]);
          }

          if (pnlRes.success) {
            const pnl = Array.isArray(pnlRes.data) ? pnlRes.data : [];
            setStrategyPnlHistory(prev => arraysEqual(prev, pnl) ? prev : pnl);
          } else {
            setStrategyPnlHistory([]);
          }
        });

        // Background KuCoin count for DB vs Live parity hint (does not switch data source)
        adminApiService.getStrategyKucoinAnalytics(selectedId, 200).then((kucoinRes) => {
          if (kucoinRes.success && kucoinRes.data?.summary?.closedPositionCount != null) {
            setLastKucoinClosedCount(Number(kucoinRes.data.summary.closedPositionCount));
          }
        }).catch(() => { /* optional */ });
      }

    } catch (error) {
      console.error('Failed to fetch strategy analytics:', error);
      if (!silentRefresh) {
      setStrategyLoading(false);
      }
    } finally {
      // Only hide loading indicator if we showed it
      if (!silentRefresh) {
      console.log('fetchStrategyAnalytics: About to set strategyLoading to false');
      
      // CRITICAL: Use setTimeout to ensure state updates happen in next tick
      // This forces React to process the state change and re-render
      setTimeout(() => {
        console.log('fetchStrategyAnalytics: Setting strategyLoading to false (after timeout)');
        setStrategyLoading(false);
        console.log('fetchStrategyAnalytics: All state updates applied, component should re-render now');
      }, 0);
      }
    }
  };

  const loadMoreKucoinTrades = async () => {
    if (!kucoinNextPageId || kucoinLoadingMore) return;
    setKucoinLoadingMore(true);
    let cursor: string | null = kucoinNextPageId;
    let hasMoreRemote = false;
    let nextCursor: string | null = null;
    const MAX_AUTO_PAGES = 5;
    const accumulatedTrades: any[] = [];
    try {
      for (let attempt = 0; attempt < MAX_AUTO_PAGES && cursor; attempt++) {
        const res = await adminApiService.getStrategyKucoinAnalytics(selectedId, 200, cursor);
        if (res.success && res.data) {
          const payload = res.data as any;
          const newTrades = Array.isArray(payload.closedTrades) ? payload.closedTrades.map((t: any) => ({
            symbol: t.symbol,
            side: t.side === 'buy' ? 'LONG' : t.side === 'sell' ? 'SHORT' : t.side?.toUpperCase(),
            amount: t.size,
            price: t.closePrice,
            entryPrice: t.openPrice,
            pnl: t.pnl,
            exitReason: null,
            closedAt: t.closeTime,
            tradeFee: t.tradeFee,
            fundingFee: t.fundingFee,
            leverage: t.leverage,
            settleCurrency: t.settleCurrency,
            openTime: t.openTime,
            closeTime: t.closeTime,
          })) : [];
          accumulatedTrades.push(...newTrades);
          // Always track the latest pagination state from the backend
          hasMoreRemote = !!payload.hasMore;
          nextCursor = payload.nextPageId || null;
          cursor = hasMoreRemote ? nextCursor : null;
          // Stop auto-advancing once we've found some trades for this strategy
          if (newTrades.length > 0) break;
        } else {
          break;
        }
      }
    } catch (e) {
      console.warn('loadMoreKucoinTrades failed:', e);
    } finally {
      // Apply all state updates atomically after the loop
      if (accumulatedTrades.length > 0) {
        setStrategyTrades(prev => [...prev, ...accumulatedTrades]);
      }
      setKucoinHasMore(hasMoreRemote);
      setKucoinNextPageId(nextCursor);
      setKucoinLoadingMore(false);
    }
  };

  /** Import missing CLOSE rows from KuCoin history-positions into trading_history, then reload DB trades. */
  const syncDbMissingFromKuCoin = async () => {
    if (dbSyncLoading) return;
    setDbSyncLoading(true);
    setDbSyncMessage(null);
    try {
      const res = await adminApiService.syncClosedTradesFromKuCoinHistory(selectedId, true);
      if (res.success && res.data) {
        const d = res.data as {
          importedCount?: number;
          updatedCount?: number;
          matchedCount?: number;
          errorCount?: number;
        };
        const imported = Number(d.importedCount ?? 0);
        const updated = Number(d.updatedCount ?? 0);
        setDbSyncMessage(
          imported > 0 || updated > 0
            ? `Synced from KuCoin: ${imported} imported, ${updated} updated. Refreshing DB trades…`
            : 'KuCoin sync complete — no new closes to import. Existing rows may already match.'
        );
        await fetchStrategyAnalytics(false, 'db');
      } else {
        setDbSyncMessage(res.error || 'KuCoin sync failed');
      }
    } catch (e: unknown) {
      setDbSyncMessage(e instanceof Error ? e.message : 'KuCoin sync failed');
    } finally {
      setDbSyncLoading(false);
    }
  };

  const fetchUsersData = async () => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const fullUrl = `${apiBaseUrl}/api/admin/analytics/${type}/${selectedId}/users?range=${timeRange}&page=${page}&size=20&sort=${sortBy}&direction=${sortDirection}`;
      
      // Get auth token from store
      const state = store.getState();
      const token = state.auth.token;
      const authType = state.auth.type;
      
      console.log('AdminAnalyticsTabs: Fetching users data from:', fullUrl);
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // Add Authorization header for JWT auth
      if (token && authType === 'JWT') {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(fullUrl, {
        credentials: 'include',
        headers,
      });
      
      console.log('AdminAnalyticsTabs: Users response status:', response.status);
      
      if (!response.ok) {
        const responseText = await response.text();
        console.error('AdminAnalyticsTabs: Users error response body:', responseText);
        throw new Error(`HTTP error! status: ${response.status}, body: ${responseText.substring(0, 200)}`);
      }
      
      const data = await response.json();
      console.log('AdminAnalyticsTabs: Users response data:', data);
      
      if (data.status === 'success' || data.status === 'Successful') {
        setUsersData(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch users data:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const toNumber = (val: any, fallback = 0): number => {
    const n = typeof val === 'number' ? val : Number(val);
    return Number.isFinite(n) ? n : fallback;
  };

  const safeFixed = (val: any, digits = 2): string => {
    const n = toNumber(val, 0);
    return n.toFixed(digits);
  };

  const formatPercentage = (value: any) => {
    const n = toNumber(value, 0);
    return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('desc');
    }
  };

  // CRITICAL: Calculate shouldShowLoading based on current state
  // Access strategyLoading directly to avoid stale closures
  // forceRender is included in render logic to ensure re-renders happen
  const currentStrategyLoading = strategyLoading;
  const shouldShowLoading = loading || (type === 'strategies' && currentStrategyLoading);
  
  // Log every render with all relevant state
  // CRITICAL: forceRender value is used here to ensure this runs on every forceRender change
  console.log('AdminAnalyticsTabs: Render check', { 
    loading, 
    type, 
    strategyLoading: currentStrategyLoading, 
    currentStrategyLoading,
    shouldShowLoading,
    strategyDetails: !!strategyDetails,
    strategyTradesCount: strategyTrades.length,
    strategyPositionsCount: strategyPositions.length,
    strategyPnlHistoryCount: strategyPnlHistory.length,
    forceRender,
    willShowLoading: shouldShowLoading,
    timestamp: new Date().toISOString()
  });
  
  // CRITICAL: Double-check the loading state before rendering
  // Use currentStrategyLoading to ensure we have the most up-to-date value
  if (shouldShowLoading) {
    console.log('AdminAnalyticsTabs: Showing loading state', { 
      loading, 
      strategyLoading: currentStrategyLoading,
      currentStrategyLoading,
      type, 
      shouldShowLoading,
      calculated: loading || (type === 'strategies' && currentStrategyLoading),
      forceRender
    });
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {type === 'bots' ? 'Bot' : 'Strategy'} Analytics
            </h2>
            <p className="text-sm text-gray-500">
              ID: {selectedId}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '8px 12px',
              borderRadius: 6,
              border: 'none',
              cursor: 'pointer',
              background: branding.primaryColor,
              color: '#ffffff'
            }}
          >
            Close
          </button>
        </div>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  // Render strategy-specific analytics  
  if (type === 'strategies') {
    console.log('AdminAnalyticsTabs: Rendering strategy analytics (past loading check)', {
      strategyDetails: strategyDetails ? `exists (id: ${strategyDetails.id || 'unknown'})` : 'null',
      strategyTrades: strategyTrades.length,
      strategyPositions: strategyPositions.length,
      strategyPnlHistory: strategyPnlHistory.length,
      strategyLoading,
      selectedId,
      forceRender,
      shouldShowLoading: loading || (type === 'strategies' && strategyLoading)
    });
    
    // Early return if we don't have strategy details yet (shouldn't happen if loading is false, but safety check)
    if (!strategyDetails) {
      console.warn('AdminAnalyticsTabs: No strategy details available but loading is false');
      return (
        <div style={{ maxWidth: 980, margin: '0 auto', padding: 16, background: '#fff', minHeight: '400px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <h2 style={{ fontSize: 24, fontWeight: 700, color: '#212529', marginBottom: 4 }}>
                Strategy Analytics
              </h2>
              <div style={{ fontSize: 12, color: '#6c757d' }}>ID: {selectedId}</div>
            </div>
            <button
              onClick={onClose}
              style={{
                padding: '8px 16px',
                borderRadius: 6,
                border: 0,
                background: branding.primaryColor,
                color: '#fff',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 500
              }}
            >
              Close
            </button>
          </div>
          <div style={{ padding: '40px 0', textAlign: 'center', color: '#6c757d', fontSize: 14 }}>
            No strategy data available
          </div>
        </div>
      );
    }
    
    let config: any = {};
    try {
      if (strategyDetails?.config) {
        config = typeof strategyDetails.config === 'string' ? JSON.parse(strategyDetails.config) : strategyDetails.config;
      }
    } catch (e) {
      console.warn('Failed to parse strategy config:', e);
      config = {};
    }
    
    const latestPnl = strategyPnlHistory.length > 0 
      ? toNumber(strategyPnlHistory[strategyPnlHistory.length - 1]?.cumulativePnl || strategyPnlHistory[strategyPnlHistory.length - 1]?.pnl || 0)
      : 0;

    // CRITICAL: Use forceRender in the return to ensure React re-renders when it changes
    // This forces React to recognize when data has loaded and update the UI
    return (
      <div 
        key={`strategy-analytics-${selectedId}-${forceRender}`}
        style={{ maxWidth: 980, margin: '0 auto', padding: 16, background: '#fff', minHeight: '400px' }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ fontSize: 24, fontWeight: 700, color: '#212529', marginBottom: 4 }}>
              {strategyDetails?.name || `Strategy ${selectedId}`}
            </h2>
            <div style={{ fontSize: 12, color: '#6c757d' }}>ID: {selectedId}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {/* Data source toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 8, padding: '4px 6px' }}>
              <span style={{ fontSize: 12, color: '#6c757d', fontWeight: 500 }}>Data:</span>
              <button
                onClick={() => { setDataSource('kucoin'); dataSourceRef.current = 'kucoin'; }}
                title="Use live KuCoin exchange data (more accurate)"
                style={{
                  padding: '8px 12px', minHeight: 36, borderRadius: 6, border: 0, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  background: dataSource === 'kucoin' ? '#0d6efd' : 'transparent',
                  color: dataSource === 'kucoin' ? '#fff' : '#495057',
                  transition: 'all 0.15s',
                }}
              >
                KuCoin Live
              </button>
              <button
                onClick={() => { setDataSource('db'); dataSourceRef.current = 'db'; }}
                title="Rollback to original DB-sourced data"
                style={{
                  padding: '8px 12px', minHeight: 36, borderRadius: 6, border: 0, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  background: dataSource === 'db' ? '#6c757d' : 'transparent',
                  color: dataSource === 'db' ? '#fff' : '#495057',
                  transition: 'all 0.15s',
                }}
              >
                DB (Rollback)
              </button>
            </div>
            <button
              onClick={onClose}
              style={{
                padding: '10px 18px', minHeight: 36,
                borderRadius: 6, border: 0,
                background: branding.primaryColor,
                color: '#fff', cursor: 'pointer',
                fontSize: 14, fontWeight: 500
              }}
            >
              Close
            </button>
          </div>
        </div>

        {/* KuCoin error banner */}
        {kucoinError && dataSource === 'kucoin' && (
          <div style={{ padding: '10px 14px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6, marginBottom: 12, fontSize: 13, color: '#856404', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>⚠️ KuCoin data unavailable: {kucoinError}. Switch to <strong>DB (Rollback)</strong> for historical data.</span>
            <button onClick={() => { setDataSource('db'); dataSourceRef.current = 'db'; }} style={{ marginLeft: 12, padding: '8px 12px', minHeight: 36, border: '1px solid #856404', borderRadius: 4, background: 'transparent', color: '#856404', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              Use DB
            </button>
          </div>
        )}

        {/* KuCoin summary bar (shown only when KuCoin source is active and summary loaded) */}
        {dataSource === 'kucoin' && kucoinSummary && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, background: '#eaf4ff', border: '1px solid #b8d4f0', borderRadius: 8, padding: '12px 14px', marginBottom: 16, fontSize: 12 }}>
            <div>
              <div style={{ color: '#5c7fa5', fontWeight: 500, marginBottom: 2 }}>Closed Positions</div>
              <div style={{ fontWeight: 700, color: '#212529', fontSize: 15 }}>{kucoinSummary.closedPositionCount}</div>
            </div>
            <div>
              <div style={{ color: '#5c7fa5', fontWeight: 500, marginBottom: 2 }}>Gross P&L (before fees)</div>
              <div style={{ fontWeight: 700, fontSize: 15, color: Number(kucoinSummary.grossPnl ?? kucoinSummary.totalPnl) >= 0 ? '#198754' : '#dc3545' }}>
                {Number(kucoinSummary.grossPnl ?? kucoinSummary.totalPnl) >= 0 ? '+' : ''}${Number(kucoinSummary.grossPnl ?? kucoinSummary.totalPnl).toFixed(4)}
              </div>
            </div>
            <div>
              <div style={{ color: '#5c7fa5', fontWeight: 500, marginBottom: 2 }}>Net P&L (after fees)</div>
              <div style={{ fontWeight: 700, fontSize: 15, color: Number(kucoinSummary.netPnlAfterFees ?? kucoinSummary.totalPnl) >= 0 ? '#198754' : '#dc3545' }}>
                {Number(kucoinSummary.netPnlAfterFees ?? kucoinSummary.totalPnl) >= 0 ? '+' : ''}${Number(kucoinSummary.netPnlAfterFees ?? kucoinSummary.totalPnl).toFixed(4)}
              </div>
            </div>
            <div>
              <div style={{ color: '#5c7fa5', fontWeight: 500, marginBottom: 2 }}>Trade Fees</div>
              <div style={{ fontWeight: 700, color: '#dc3545', fontSize: 15 }}>-${Math.abs(Number(kucoinSummary.totalTradeFee)).toFixed(4)}</div>
            </div>
            <div>
              <div style={{ color: '#5c7fa5', fontWeight: 500, marginBottom: 2 }}>Funding Fees</div>
              <div style={{ fontWeight: 700, color: '#dc3545', fontSize: 15 }}>-${Math.abs(Number(kucoinSummary.totalFundingFee)).toFixed(4)}</div>
            </div>
            <div>
              <div style={{ color: '#5c7fa5', fontWeight: 500, marginBottom: 2 }}>Win Rate</div>
              <div style={{ fontWeight: 700, color: '#0d6efd', fontSize: 15 }}>{Number(kucoinSummary.winRate).toFixed(1)}%</div>
            </div>
            <div>
              <div style={{ color: '#5c7fa5', fontWeight: 500, marginBottom: 2 }}>W / L</div>
              <div style={{ fontWeight: 700, color: '#212529', fontSize: 15 }}>{kucoinSummary.winCount} / {kucoinSummary.lossCount}</div>
            </div>
            <div>
              <div style={{ color: '#5c7fa5', fontWeight: 500, marginBottom: 2 }}>Source</div>
              <div style={{ display: 'inline-block', padding: '2px 8px', background: '#0d6efd', color: '#fff', borderRadius: 4, fontWeight: 600, fontSize: 11 }}>🔴 LIVE</div>
            </div>
          </div>
        )}

        {/* DB rollback: show count vs KuCoin + import missing closes */}
        {dataSource === 'db' && !strategyLoading && (
          <div
            style={{
              background: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: 8,
              padding: '10px 14px',
              marginBottom: 12,
              fontSize: 12,
              color: '#495057',
            }}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <strong>DB rollback:</strong>{' '}
                {dbTradeMeta?.totalCloseRowsInDb != null
                  ? `${dbTradeMeta.totalCloseRowsInDb} CLOSE row(s) in trading_history`
                  : `${strategyTrades.length} trade(s) loaded`}
                {dbTradeMeta?.returnedCount != null && dbTradeMeta.totalCloseRowsInDb != null
                  && dbTradeMeta.returnedCount < dbTradeMeta.totalCloseRowsInDb
                  ? ` · showing ${dbTradeMeta.returnedCount} (limit ${dbTradeMeta.limit ?? 200})`
                  : ''}
                {(() => {
                  const kucoinCount = kucoinSummary?.closedPositionCount ?? lastKucoinClosedCount;
                  if (kucoinCount == null || dbTradeMeta?.totalCloseRowsInDb == null) return null;
                  const diff = Number(kucoinCount) - Number(dbTradeMeta.totalCloseRowsInDb);
                  if (diff <= 0) return null;
                  return (
                    <span style={{ color: '#856404', marginLeft: 6 }}>
                      · KuCoin Live has {kucoinCount} closes — DB may be missing {diff}
                    </span>
                  );
                })()}
              </div>
              <button
                type="button"
                onClick={syncDbMissingFromKuCoin}
                disabled={dbSyncLoading}
                style={{
                  padding: '6px 12px',
                  borderRadius: 6,
                  border: '1px solid #6c757d',
                  background: '#fff',
                  color: '#495057',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: dbSyncLoading ? 'wait' : 'pointer',
                  whiteSpace: 'nowrap',
                }}
                title="Pull missing closed positions from KuCoin history-positions into trading_history"
              >
                {dbSyncLoading ? 'Syncing…' : 'Import missing from KuCoin'}
              </button>
            </div>
            {dbSyncMessage && (
              <div style={{ marginTop: 8, fontSize: 11, color: '#0d6efd' }}>{dbSyncMessage}</div>
            )}
            <div style={{ marginTop: 6, fontSize: 11, color: '#6c757d' }}>
              DB shows only <code style={{ fontSize: 10 }}>trade_type=CLOSE</code> rows saved when the bot closed or reconciled.
              If counts differ, use Import — or check SQL:{' '}
              <code style={{ fontSize: 10 }}>SELECT id,symbol,closed_at,pnl FROM trading_history WHERE strategy_id={selectedId} AND trade_type=&apos;CLOSE&apos; ORDER BY closed_at DESC</code>
            </div>
          </div>
        )}

        {/* Rolling-window trade evaluation (KuCoin Live + DB Rollback) */}
        {!strategyLoading && (
          <TradeEvaluationPanel
            trades={mapTradesForEvaluation(strategyTrades, dataSource)}
            fixedOrderAmount={Number(config.fixedOrderAmount) > 0 ? Number(config.fixedOrderAmount) : undefined}
            hasMoreRemote={dataSource === 'kucoin' && kucoinHasMore}
            defaultCount={12}
          />
        )}

        {/* Strategy Overview */}
        <div style={{ background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 8, padding: 16, marginBottom: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#212529' }}>Strategy Overview</div>
          
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 13, color: '#6c757d', marginBottom: 4 }}>Exchanges:</div>
            <StrategyExchangeInfo sagemasterEnabled={sagemasterEnabled} variant="inline" />
          </div>

          {/* Parameters */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 13, color: '#6c757d', marginBottom: 4 }}>Leverage:</div>
              <div style={{ fontSize: 14, fontWeight: 500, color: '#212529' }}>{config.leverage || 1}x</div>
            </div>
            <div>
              <div style={{ fontSize: 13, color: '#6c757d', marginBottom: 4 }}>Order:</div>
              <div style={{ fontSize: 14, fontWeight: 500, color: '#212529' }}>${config.fixedOrderAmount || 0}</div>
            </div>
            <div>
              <div style={{ fontSize: 13, color: '#6c757d', marginBottom: 4 }}>TP:</div>
              <div style={{ fontSize: 14, fontWeight: 500, color: '#212529' }}>{(config.takeProfit || 0) * 100}%</div>
            </div>
            <div>
              <div style={{ fontSize: 13, color: '#6c757d', marginBottom: 4 }}>TSL:</div>
              <div style={{ fontSize: 14, fontWeight: 500, color: '#212529' }}>{(config.trailingStopLoss || 0) * 100}%</div>
            </div>
          </div>

          {/* P&L Trend (mini chart) */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 13, color: '#6c757d' }}>P&L trend:</div>
              <button style={{ fontSize: 12, color: '#6c757d', background: 'none', border: 0, cursor: 'pointer' }}>Hide</button>
            </div>
            {strategyPnlHistory.length > 0 && (
              <div style={{ height: 40, position: 'relative' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={strategyPnlHistory.map(d => ({ ...d, value: toNumber(d.cumulativePnl || d.pnl || 0) }))}>
                    <Line type="monotone" dataKey="value" stroke="#dc3545" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Strategy Description */}
          <div>
            <div style={{ fontSize: 13, color: '#6c757d', marginBottom: 4 }}>Strategy Description:</div>
            <div style={{ fontSize: 14, color: '#495057', lineHeight: 1.5 }}>
              This strategy trades pairs of meme coins simultaneously - going LONG on one coin and SHORT on another. It profits from relative price movements between the coins, making it market-neutral. Perfect for volatile meme coin markets.
            </div>
          </div>
        </div>

        {/* Strategy Analytics Section */}
        <div>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 16, color: '#212529' }}>Strategy Analytics</div>

          {/* Recent Trades and Open Positions in Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: 16, marginBottom: 16 }}>
            {/* Recent Trades */}
            <div style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: 8, padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#212529' }}>
                  {dataSource === 'kucoin' ? 'Closed Trades (KuCoin)' : 'Recent Trades (DB)'}
                </div>
                {dataSource === 'kucoin' && (
                  <span style={{ fontSize: 11, padding: '2px 7px', background: '#d1e7ff', color: '#084298', borderRadius: 4, fontWeight: 600 }}>LIVE</span>
                )}
              </div>
              <div style={{ maxHeight: 300, overflowY: 'auto', overflowX: 'auto', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}>
                <table style={{ width: '100%', fontSize: 13, minWidth: 280 }}>
                  <thead style={{ borderBottom: '1px solid #e9ecef' }}>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '8px 0', color: '#6c757d', fontWeight: 500 }}>Trade</th>
                      <th style={{ textAlign: 'right', padding: '8px 0', color: '#6c757d', fontWeight: 500 }}>
                        {dataSource === 'kucoin' ? 'Size @ Entry → Exit' : 'Amount @ Price'}
                      </th>
                      {dataSource === 'kucoin' && (
                        <th style={{ textAlign: 'right', padding: '8px 0', color: '#6c757d', fontWeight: 500 }}>Fees</th>
                      )}
                      <th style={{ textAlign: 'right', padding: '8px 0', color: '#6c757d', fontWeight: 500 }}>P&L</th>
                      <th style={{ textAlign: 'right', padding: '8px 0', color: '#6c757d', fontWeight: 500 }}>Closed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {strategyTrades.length > 0 ? [...strategyTrades].sort((a, b) => {
                      const tA = a.closeTimeMs != null ? Number(a.closeTimeMs) : (a.closeTime ? new Date(a.closeTime).getTime() : (a.closedAt ? new Date(a.closedAt).getTime() : 0));
                      const tB = b.closeTimeMs != null ? Number(b.closeTimeMs) : (b.closeTime ? new Date(b.closeTime).getTime() : (b.closedAt ? new Date(b.closedAt).getTime() : 0));
                      return tB - tA; // newest first
                    }).map((trade, idx) => {
                      const pnl = toNumber(trade.pnl || 0);
                      const totalFee = dataSource === 'kucoin'
                        ? toNumber(trade.tradeFee || 0) + toNumber(trade.fundingFee || 0)
                        : 0;
                      const closedDate = formatTradeClosedDisplay(trade);
                      return (
                        <tr key={idx} style={{ borderBottom: '1px solid #f1f3f5' }}>
                          <td style={{ padding: '8px 0', color: '#212529' }}>
                            <span>{trade.symbol}</span>{' '}
                            <span style={{ color: trade.side === 'LONG' || trade.side === 'buy' ? '#198754' : '#dc3545', fontWeight: 600 }}>{trade.side}</span>
                          </td>
                          <td style={{ textAlign: 'right', padding: '8px 0', color: '#6c757d' }}>
                            {dataSource === 'kucoin' ? (
                              <span>
                                {trade.amount} @ ${toNumber(trade.entryPrice || trade.price || 0).toFixed(4)} → ${toNumber(trade.price || 0).toFixed(4)}
                              </span>
                            ) : trade.side === 'CLOSE' && trade.entryPrice ? (() => {
                              const amount = toNumber(trade.amount || 0);
                              const entryPrice = toNumber(trade.entryPrice || 0);
                              const exitPrice = toNumber(trade.price || 0);
                              const notionalValueAtExit = entryPrice > 0 ? amount * (exitPrice / entryPrice) : 0;
                              return (
                                <span>
                                  ${amount.toFixed(2)} @ Entry: ${entryPrice.toFixed(6)} → Exit: ${exitPrice.toFixed(6)} (Value: ${notionalValueAtExit.toFixed(2)})
                                </span>
                              );
                            })() : (
                              <span>
                                ${toNumber(trade.amount || 0).toFixed(2)} @ ${toNumber(trade.price || 0).toFixed(6)}
                              </span>
                            )}
                          </td>
                          {dataSource === 'kucoin' && (
                            <td style={{ textAlign: 'right', padding: '8px 0', color: '#dc3545', fontSize: 12 }}>
                              {totalFee !== 0 ? `-$${Math.abs(totalFee).toFixed(4)}` : '—'}
                            </td>
                          )}
                          <td style={{ textAlign: 'right', padding: '8px 0', fontWeight: 600, color: pnl >= 0 ? '#198754' : '#dc3545' }}>
                            {(() => {
                              if (Math.abs(pnl) < 0.01 && pnl !== 0) {
                                return (pnl >= 0 ? '+' : '') + '$' + pnl.toFixed(6);
                              }
                              return (pnl >= 0 ? '+' : '') + formatCurrency(pnl);
                            })()}
                          </td>
                          <td style={{ textAlign: 'right', padding: '8px 0', color: '#6c757d', fontSize: 11, whiteSpace: 'nowrap' }}>
                            {closedDate}
                          </td>
                        </tr>
                      );
                    }) : (
                      <tr>
                        <td colSpan={dataSource === 'kucoin' ? 5 : 4} style={{ padding: '16px 0', textAlign: 'center', color: '#6c757d', fontSize: 12 }}>
                          {dataSource === 'kucoin' ? 'No closed trades found for this strategy in KuCoin' : 'No trades yet'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {/* Load More older trades — only shown in KuCoin mode when there are more pages */}
              {dataSource === 'kucoin' && kucoinHasMore && (
                <div style={{ textAlign: 'center', paddingTop: 12 }}>
                  <button
                    onClick={loadMoreKucoinTrades}
                    disabled={kucoinLoadingMore}
                    style={{
                      padding: '6px 20px',
                      fontSize: 13,
                      fontWeight: 600,
                      background: kucoinLoadingMore ? '#e9ecef' : '#086afb',
                      color: kucoinLoadingMore ? '#6c757d' : '#fff',
                      border: 'none',
                      borderRadius: 6,
                      cursor: kucoinLoadingMore ? 'not-allowed' : 'pointer',
                      transition: 'opacity 0.15s',
                    }}
                  >
                    {kucoinLoadingMore ? 'Loading…' : 'Load older trades'}
                  </button>
                </div>
              )}
            </div>

            {/* Open Positions */}
            <div style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: 8, padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#212529' }}>Open Positions</div>
                  <span style={{ fontSize: 11, padding: '2px 6px', background: '#e9ecef', color: '#495057', borderRadius: 4, fontWeight: 500 }}>
                    Live DB
                  </span>
                </div>
                <span style={{ fontSize: 11, color: '#6c757d', fontStyle: 'italic' }}>
                  Active pairs held right now
                </span>
              </div>
              <div style={{ maxHeight: 300, overflowY: 'auto', overflowX: 'auto', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}>
                <table style={{ width: '100%', fontSize: 13, minWidth: 260 }}>
                  <thead style={{ borderBottom: '1px solid #e9ecef' }}>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '8px 0', color: '#6c757d', fontWeight: 500 }}>Position</th>
                      <th style={{ textAlign: 'right', padding: '8px 0', color: '#6c757d', fontWeight: 500 }}>Amount @ Entry</th>
                      <th style={{ textAlign: 'right', padding: '8px 0', color: '#6c757d', fontWeight: 500 }}>Unrealised P&L</th>
                    </tr>
                  </thead>
                  <tbody>
                    {strategyPositions.length > 0 ? strategyPositions.map((pos, idx) => {
                      const pnl = toNumber(pos.unrealizedPnl || 0);
                      const currentPrice = toNumber(pos.currentPrice || 0);
                      return (
                        <tr key={idx} style={{ borderBottom: '1px solid #f1f3f5' }}>
                          <td style={{ padding: '8px 0', color: '#212529', fontWeight: 500 }}>
                            {pos.symbol}
                            <span style={{
                              marginLeft: 6, fontSize: 11, padding: '2px 6px', borderRadius: 3, fontWeight: 600,
                              background: pos.side === 'LONG' ? '#d1e7dd' : '#f8d7da',
                              color: pos.side === 'LONG' ? '#0f5132' : '#842029'
                            }}>{pos.side}</span>
                          </td>
                          <td style={{ textAlign: 'right', padding: '8px 0', color: '#6c757d' }}>
                            ${toNumber(pos.amount || 0).toFixed(2)} @ ${toNumber(pos.entryPrice || 0).toFixed(4)}
                            {currentPrice > 0 && (
                              <div style={{ fontSize: 11, color: '#adb5bd' }}>
                                Now: ${currentPrice.toFixed(4)}
                              </div>
                            )}
                          </td>
                          <td style={{ textAlign: 'right', padding: '8px 0', fontWeight: 600, color: pnl >= 0 ? '#198754' : '#dc3545' }}>
                            {pnl >= 0 ? '+' : ''}{formatCurrency(pnl)}
                          </td>
                        </tr>
                      );
                    }) : (
                      <tr>
                        <td colSpan={3} style={{ padding: '20px 0', textAlign: 'center' }}>
                          <div style={{ color: '#6c757d', fontSize: 12 }}>No open positions right now</div>
                          <div style={{ color: '#adb5bd', fontSize: 11, marginTop: 4 }}>
                            Positions appear here when the bot has active long/short trades on the exchange
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* P&L History */}
          <div style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: 8, padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#212529' }}>
                {dataSource === 'kucoin' ? 'P&L History (KuCoin — all time)' : '30-Day P&L History (DB)'}
              </div>
              {dataSource === 'kucoin' && (
                <span style={{ fontSize: 11, padding: '2px 7px', background: '#d1e7ff', color: '#084298', borderRadius: 4, fontWeight: 600 }}>LIVE</span>
              )}
            </div>
            {strategyPnlHistory.length > 0 ? (
              <>
                <div style={{ height: 200, marginBottom: 12 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={strategyPnlHistory.map(d => ({ ...d, value: toNumber(d.cumulativePnl || d.pnl || 0) }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12, fill: '#6c757d' }}
                        tickFormatter={(value) => {
                          const date = new Date(value);
                          return `${date.getMonth() + 1}/${date.getDate()}`;
                        }}
                      />
                      <YAxis tick={{ fontSize: 12, fill: '#6c757d' }} tickFormatter={(value) => `$${value.toFixed(0)}`} />
                      <Tooltip 
                        formatter={(value: number) => [formatCurrency(value), 'P&L']}
                        labelFormatter={(value) => value}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="value" 
                        stroke="#dc3545" 
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: latestPnl >= 0 ? '#198754' : '#dc3545', textAlign: 'right' }}>
                  Total P&L: {latestPnl >= 0 ? '+' : ''}{formatCurrency(latestPnl)}
                </div>
              </>
            ) : (
              <div style={{ padding: '40px 0', textAlign: 'center', color: '#6c757d', fontSize: 14 }}>
                No P&L history available
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {type === 'bots' ? 'Bot' : 'Strategy'} Analytics
            </h2>
            <p className="text-sm text-gray-500">
              ID: {selectedId}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '8px 12px',
              borderRadius: 6,
              border: 'none',
              cursor: 'pointer',
              background: branding.primaryColor,
              color: '#ffffff'
            }}
          >
            Close
          </button>
        </div>
        <div className="text-center py-8">
          <p className="text-gray-500">No analytics data available</p>
          <p className="text-sm text-gray-400 mt-2">
            This {type === 'bots' ? 'bot' : 'strategy'} may not have any trading data yet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', padding: 16 }}>
      {/* Header */}
      <div style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: 12, padding: '16px 18px', marginBottom: 16, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#212529' }}>{analyticsData.name} • Analytics</div>
            <div style={{ fontSize: 12, color: '#6c757d', marginTop: 4 }}>Last updated: {formatDate(analyticsData.lastUpdated)}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)} aria-label="Select time range"
              style={{ padding: '8px 10px', border: '1px solid #dee2e6', borderRadius: 6, fontSize: 13, background: '#fff' }}>
              <option value="1D">1 Day</option>
              <option value="1W">1 Week</option>
              <option value="1M">1 Month</option>
              <option value="3M">3 Months</option>
              <option value="1Y">1 Year</option>
            </select>
            <button onClick={onClose} style={{ padding: '8px 12px', borderRadius: 6, border: 0, color: '#fff', background: branding.primaryColor, fontSize: 13, cursor: 'pointer' }}>Close</button>
          </div>
        </div>
        {/* Tabs */}
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'inline-flex', background: '#f1f3f5', padding: 4, borderRadius: 10 }}>
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'users', label: 'Users' },
              { id: 'risk', label: 'Risk' },
              { id: 'distributions', label: 'Distributions' }
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id as any)}
                style={{
                  padding: '8px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: 0, cursor: 'pointer',
                  background: activeTab === tab.id ? '#fff' : 'transparent',
                  boxShadow: activeTab === tab.id ? '0 1px 2px rgba(0,0,0,0.04)' : 'none',
                  color: activeTab === tab.id ? '#212529' : '#495057'
                }}>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="text-sm font-semibold text-gray-700">Strategy Overview</div>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg border border-blue-100">
              <h3 className="text-sm font-medium text-blue-600">Total Invested</h3>
              <p className="text-2xl font-bold text-blue-900">{formatCurrency(analyticsData.totalInvested)}</p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-green-100">
              <h3 className="text-sm font-medium text-green-600">Current Value</h3>
              <p className="text-2xl font-bold text-green-900">{formatCurrency(analyticsData.currentValue)}</p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-purple-100">
              <h3 className="text-sm font-medium text-purple-600">Total P&L</h3>
              <p className={`text-2xl font-bold ${analyticsData.totalPnl >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                {formatCurrency(analyticsData.totalPnl)} ({formatPercentage(analyticsData.pnlPercentage)})
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-orange-100">
              <h3 className="text-sm font-medium text-orange-600">Active Users</h3>
              <p className="text-2xl font-bold text-orange-900">{analyticsData.activeUsers}</p>
            </div>
          </div>

          {/* Equity Curve Chart */}
          <div className="bg-white p-4 rounded-lg border border-gray-100">
            <h3 className="text-lg font-semibold mb-4">Equity Curve</h3>
            {(!analyticsData.equityCurve || analyticsData.equityCurve.length === 0) ? (
              <div className="h-48 flex items-center justify-center text-sm text-gray-500">
                No equity data for the selected range.
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analyticsData.equityCurve}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="timestamp" 
                      tickFormatter={(value) => formatDate(value)}
                    />
                    <YAxis tickFormatter={(value) => formatCurrency(value)} />
                    <Tooltip 
                      formatter={(value: number) => [formatCurrency(value), 'Value']}
                      labelFormatter={(value) => formatDate(value)}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#667eea" 
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-semibold text-gray-700">User Leaderboard</h3>
            <div className="flex space-x-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="totalPnl">Total P&L</option>
                <option value="pnlPercentage">P&L %</option>
                <option value="allocatedAmount">Allocated</option>
                <option value="currentValue">Current Value</option>
              </select>
              <button
                onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                {sortDirection === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto bg-white rounded-lg border border-gray-100">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('allocatedAmount')}
                  >
                    Allocated
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('currentValue')}
                  >
                    Current Value
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('totalPnl')}
                  >
                    P&L
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('pnlPercentage')}
                  >
                    P&L %
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {usersData.map((user) => (
                  <tr key={user.userId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.username}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(user.allocatedAmount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(user.currentValue)}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${
                      user.totalPnl >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatCurrency(user.totalPnl)}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${
                      user.pnlPercentage >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatPercentage(user.pnlPercentage)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        user.status === 'ACTIVE' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {user.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex justify-between items-center bg-gray-50 px-4 py-2 rounded-md">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-700">Page {page + 1}</span>
            <button
              onClick={() => setPage(page + 1)}
              className="px-4 py-2 border border-gray-300 rounded-md"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Risk Tab */}
      {activeTab === 'risk' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg border border-red-100">
              <h3 className="text-sm font-medium text-red-600">Max Drawdown</h3>
              <p className="text-2xl font-bold text-red-900">{formatPercentage(analyticsData.maxDrawdown)}</p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-yellow-100">
              <h3 className="text-sm font-medium text-yellow-600">Volatility</h3>
              <p className="text-2xl font-bold text-yellow-900">{safeFixed(analyticsData.riskMetrics?.volatility, 1)}%</p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-blue-100">
              <h3 className="text-sm font-medium text-blue-600">Win Rate</h3>
              <p className="text-2xl font-bold text-blue-900">{safeFixed(analyticsData.riskMetrics?.winRate, 1)}%</p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-green-100">
              <h3 className="text-sm font-medium text-green-600">Sharpe Ratio</h3>
              <p className="text-2xl font-bold text-green-900">{safeFixed(analyticsData.riskMetrics?.sharpeRatio, 2)}</p>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-100">
            <h3 className="text-lg font-semibold mb-4">Risk Metrics</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium text-gray-700">Profit Factor</h4>
                <p className="text-2xl font-bold text-gray-900">{safeFixed(analyticsData.riskMetrics?.profitFactor, 2)}</p>
              </div>
              <div>
                <h4 className="font-medium text-gray-700">Realized P&L</h4>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(toNumber(analyticsData.realizedPnl, 0))}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Distributions Tab */}
      {activeTab === 'distributions' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-lg border border-gray-100">
              <h3 className="text-lg font-semibold mb-4">P&L Distribution</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analyticsData.pnlDistribution}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="range" />
                    <YAxis />
                    <Tooltip formatter={(value: number) => [value, 'Users']} />
                    <Bar dataKey="count" fill="#667eea" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg border border-gray-100">
              <h3 className="text-lg font-semibold mb-4">P&L Distribution (Pie)</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={analyticsData.pnlDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ range, percentage }: any) => `${range}: ${(percentage as number).toFixed(1)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {analyticsData.pnlDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAnalyticsTabs;
